import { ObservableMap, action, computed, flow, makeObservable, observable, override, reaction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import moment from 'moment';
import { getOrderDeliveryType, getOrderingChannel, getOrderPaymentType } from '../ordersHistoryStore/types';
import { BaseOrdersStore } from './baseOrdersStore';
import { OrderTabs } from './types';
import { DateRanges } from '~/constants/dateTime';
import { ORDER_NOTIFICATION_TYPE, ORDER_STATUS } from '~/constants/order';
import { USER_PERMISSIONS } from '~/constants/user';
import player from '~/services/audio';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';
import { orderCast, TOrder, TOrderCRUDEvents } from '~/types/orders';
import { hasPermissions } from '~/utils/helpers';
import { AdvancedFilterStore } from '~/view/components/AdvancedFilter';
import { AdvancedFilterProperties } from '~/view/components/AdvancedFilter/types';
import { IBranchesModalItem } from '~/view/containers/Modals/BranchesModal/BranchesModal';

export type LiveOrdersTab =
  | OrderTabs.All
  | OrderTabs.New
  | OrderTabs.Processing
  | OrderTabs.Ready
  | OrderTabs.InDelivery
  | OrderTabs.Closed
  | OrderTabs.ActionRequired
  | OrderTabs.Ahead;

const SOUND_TIMEOUT_MAP: {
  [key: string]: number;
} = {
  ahead: 36000000,
  created: 36000000,
  updated: 36000000,
  client_arrived: 3000,
  driver_not_found: 3000,
};

const tabs = {
  [OrderTabs.All]: {
    key: OrderTabs.All,
    label: 'status_all',
    newOrders: observable.map(),
  },
  [OrderTabs.New]: {
    key: OrderTabs.New,
    label: 'status_new',
    newOrders: observable.map(),
  },
  [OrderTabs.Processing]: {
    key: OrderTabs.Processing,
    label: 'processing',
    newOrders: observable.map(),
  },
  [OrderTabs.Ready]: {
    key: OrderTabs.Ready,
    label: 'status_ready',
    newOrders: observable.map(),
  },
  [OrderTabs.InDelivery]: {
    key: OrderTabs.InDelivery,
    label: 'status_in_delivery',
    newOrders: observable.map(),
  },
  [OrderTabs.Closed]: {
    key: OrderTabs.Closed,
    label: 'closed_tab',
    newOrders: observable.map(),
  },
  [OrderTabs.ActionRequired]: {
    key: OrderTabs.ActionRequired,
    label: 'action_required_tab',
    newOrders: observable.map(),
  },
  [OrderTabs.Ahead]: {
    key: OrderTabs.Ahead,
    label: 'ahead',
    newOrders: observable.map(),
  },
};

export const statusByTab = (showClosed = false, separateAhead = false) => ({
  [OrderTabs.All]: [
    ORDER_STATUS.PENDING,
    ORDER_STATUS.ACCEPTED,
    ORDER_STATUS.PROCESSING,
    ORDER_STATUS.ASSIGNED,
    ORDER_STATUS.WAITING_FOR_PICKUP,
    ORDER_STATUS.OUT_FOR_DELIVERY,
    ORDER_STATUS.PICKEDUP,
    ORDER_STATUS.RETURNED,
    ...(showClosed ? [ORDER_STATUS.CANCELED, ORDER_STATUS.COMPLETED] : []),
  ],
  [OrderTabs.New]: [ORDER_STATUS.PENDING, ORDER_STATUS.ACCEPTED],
  [OrderTabs.Processing]: [ORDER_STATUS.PROCESSING],
  [OrderTabs.Ready]: [ORDER_STATUS.WAITING_FOR_PICKUP],
  [OrderTabs.InDelivery]: [ORDER_STATUS.ASSIGNED, ORDER_STATUS.PICKEDUP, ORDER_STATUS.OUT_FOR_DELIVERY],
  [OrderTabs.Closed]: showClosed ? [ORDER_STATUS.CANCELED, ORDER_STATUS.COMPLETED] : [],
  [OrderTabs.ActionRequired]: [ORDER_STATUS.RETURNED],
  [OrderTabs.Ahead]: separateAhead
    ? [
        ORDER_STATUS.PENDING,
        ORDER_STATUS.ACCEPTED,
        ORDER_STATUS.PROCESSING,
        ORDER_STATUS.ASSIGNED,
        ORDER_STATUS.WAITING_FOR_PICKUP,
        ORDER_STATUS.OUT_FOR_DELIVERY,
        ORDER_STATUS.PICKEDUP,
        ORDER_STATUS.RETURNED,
      ]
    : [],
});

export const aheadByTab = (separateAhead: boolean) => ({
  [OrderTabs.All]: undefined,
  [OrderTabs.New]: separateAhead ? 1 : undefined,
  [OrderTabs.Processing]: 1,
  [OrderTabs.Ready]: 1,
  [OrderTabs.InDelivery]: 1,
  [OrderTabs.Closed]: undefined,
  [OrderTabs.ActionRequired]: 1,
  [OrderTabs.Ahead]: 0,
});

export const getFilterByTab = (tab: LiveOrdersTab, showClosed = false, separateAhead = false) => {
  return {
    status: statusByTab(showClosed, separateAhead)[tab],
    ahead: aheadByTab(separateAhead)[tab],
  };
};

export const getOrderEventByNotificationType = (notificationType: ORDER_NOTIFICATION_TYPE | undefined): TOrderCRUDEvents | undefined => {
  switch (notificationType) {
    case ORDER_NOTIFICATION_TYPE.NEW:
      return 'created';
    case ORDER_NOTIFICATION_TYPE.DRIVER_NOT_FOUND:
      return 'driver_not_found';
    case ORDER_NOTIFICATION_TYPE.CLIENT_ARRIVED:
      return 'client_arrived';
    default:
      return undefined;
  }
};

export class LiveOrdersStore extends BaseOrdersStore {
  constructor() {
    super();
    makeObservable(this, {
      _addOrderToSound: action,
      _checkOrderMeetsFilters: override,
      _clearOrdersForSound: action,
      _ignoreSoundOrdersIds: observable,
      _makeFetchOrdersParams: override,
      _makeSoundOrdersIds: observable,
      _prevCountersResponse: observable,
      _removeOrderForSound: action,
      _soundPlayTimeout: observable,
      _tabs: observable,
      activeTabKey: observable,
      checkOrderMeetsCurrentTabAndFilters: override,
      fetchInitCounters: action,
      refreshDataAfterSocketReconnect: action,
      handleAdvancedFilter: action,
      handleUpdateOrder: override,
      ignoreOrderForSound: action,
      markOrderViewed: action,
      notViewedOrdersExists: computed,
      selectBranches: action,
      selectedBranches: observable,
      setActiveTab: action,
      soundPlayTimeout: computed,
      tabs: computed,
    });
  }

  hydrate = (uniqueKey: string = '') => {
    return Promise.all([
      makePersistable(this, {
        name: 'live_orders_store_' + uniqueKey,
        properties: ['selectedBranches'],
        storage: window.localStorage,
      }),
      this.advancedFilter.hydrate(uniqueKey),
    ]);
  };

  initTabCountersAbortController = new AbortController();
  tabCountersStatus = LoadingStatus.Init;

  _soundPlayTimeout: number = 0;

  _makeSoundOrdersIds = observable.map();
  _ignoreSoundOrdersIds = observable.map();

  _tabs = tabs;
  isCallCenterScreen = false;

  _prevCountersResponse: { [key in ORDER_STATUS]?: ObservableMap<number, { order_id: number; ahead: number }> } = {};

  get tabs() {
    return Object.entries(this._tabs).filter(
      ([tabKey]) =>
        (tabKey !== OrderTabs.Closed || this.advancedFilter.values.SHOW_CLOSED) &&
        (tabKey !== OrderTabs.Ahead || this.advancedFilter.values.SEPARATE_AHEAD)
    );
  }

  activeTabKey: LiveOrdersTab = OrderTabs.All;

  setActiveTab = (activeTab: LiveOrdersTab) => {
    this.activeTabKey = activeTab;
    this._getOrdersWithNoDelay(0);
  };

  selectedBranches: IBranchesModalItem[] = [];

  selectBranches = (branches: IBranchesModalItem[]) => {
    this.selectedBranches = branches;
    this._getOrdersWithNoDelay(0);
    this.fetchInitCounters();
  };

  advancedFilter: AdvancedFilterStore = new AdvancedFilterStore({
    name: 'live_orders',
    parentStore: this,
    properties: [
      AdvancedFilterProperties.Date,
      AdvancedFilterProperties.OrderType,
      AdvancedFilterProperties.OrderingChannel,
      AdvancedFilterProperties.PaymentMethod,
      AdvancedFilterProperties.ShowClosed,
      AdvancedFilterProperties.SeparateAhead,
    ],
    presetDates: [
      DateRanges.Tomorrow,
      // DateRanges.Today,
      DateRanges.TodayRamadan5AMto5AM,
      DateRanges.Yesterday,
      // DateRanges.ThisWeekUntilToday,
      DateRanges.ThisWeekUntilTodayRamadan5AMto5AM,
      // DateRanges.ThisMonthUntilToday,
      DateRanges.ThisMonthUntilTodayRamadan5AMto5AM,
      DateRanges.Next30Days,
      DateRanges.Next90Days,
    ],
    allowDatesInTheFuture: true,
    defaultDatePreset: DateRanges.ThisWeekUntilTodayRamadan5AMto5AM,
  });

  _makeFetchOrdersParams = (offset?: number, per_page: number = 25) => {
    const branches = this.selectedBranches.map(({ id }) => id);
    // // TOFIX
    // const branches = selectedBranchesIds.length > 0 ? selectedBranchesIds : userStore.user.branches.map(({ id }) => id);

    let currentOffset = 0;
    if (typeof offset === 'number') {
      currentOffset = offset;
    } else if (typeof this.currentOffset === 'number') {
      currentOffset = this.currentOffset + per_page;
    }
    const params = this.searchValue
      ? {
          ...getFilterByTab(OrderTabs.All, true, false),
          offset: currentOffset,
          per_page,
          branches,
          search: this.searchValue,
          is_delete: 0,
        }
      : {
          ...getFilterByTab(this.activeTabKey, this.advancedFilter.values.SHOW_CLOSED, this.advancedFilter.values.SEPARATE_AHEAD),
          offset: currentOffset,
          per_page,
          branches,
          is_delete: 0,
          due_start_date: this.advancedFilter.dateValues?.from,
          due_end_date: this.advancedFilter.dateValues?.to,
          payment_types: this.advancedFilter.values.PAYMENT_METHOD,
          delivery_types: this.advancedFilter.values.ORDER_TYPE,
          order_channels: this.advancedFilter.values.ORDERING_CHANNEL,
        };

    return params;
  };

  _checkOrderMeetsFilters(order: TOrder): boolean {
    if (
      this.advancedFilter.dateValues?.from &&
      this.advancedFilter.dateValues?.to &&
      !moment(order.order_datetime).isBetween(moment(this.advancedFilter.dateValues.from), moment(this.advancedFilter.dateValues.to), undefined, '[]')
    ) {
      return false;
    }

    if (
      this.advancedFilter.values.ORDERING_CHANNEL?.length &&
      this.advancedFilter.values.ORDERING_CHANNEL.length > 0 &&
      !this.advancedFilter.values.ORDERING_CHANNEL?.includes(
        getOrderingChannel(order.created_by_code, order.aggregator_key as number, order.dinein?.dinein_delivery_type)
      )
    ) {
      return false;
    }

    if (
      this.advancedFilter.values.ORDER_TYPE?.length &&
      this.advancedFilter.values.ORDER_TYPE.length > 0 &&
      !this.advancedFilter.values.ORDER_TYPE?.includes(
        getOrderDeliveryType(order.delivery_type_code, order.dinein?.dinein_delivery_type, !!order.curbside)
      )
    ) {
      return false;
    }

    if (
      this.advancedFilter.values.PAYMENT_METHOD?.length &&
      this.advancedFilter.values.PAYMENT_METHOD.length > 0 &&
      !this.advancedFilter.values.PAYMENT_METHOD?.includes(getOrderPaymentType(order.payment_type, order.aggregator_key as number))
    ) {
      return false;
    }

    if (order.branch?.id && this.selectedBranches.length !== 0 && !this.selectedBranches.map(({ id }) => id).includes(order.branch.id)) {
      return false;
    }

    return true;
  }

  _checkOrderMeetsTabFilter = ({ ahead, status }: { ahead: number; status: ORDER_STATUS }, tabKey = this.activeTabKey) => {
    const filterByTab = getFilterByTab(tabKey, this.advancedFilter.values.SHOW_CLOSED, this.advancedFilter.values.SEPARATE_AHEAD);
    return (filterByTab.ahead === undefined || filterByTab.ahead === ahead) && filterByTab.status.includes(status);
  };

  checkOrderMeetsCurrentTabAndFilters(order: TOrder) {
    return this._checkOrderMeetsFilters(order) && this._checkOrderMeetsTabFilter({ ahead: order.ahead, status: order.order_status_code });
  }

  handleAdvancedFilter = () => {
    const isClosedTabActiveButClosedDisabled = this.activeTabKey === OrderTabs.Closed && !this.advancedFilter.values.SHOW_CLOSED;
    const isAheadTabActiveButAheadNotSeparated = this.activeTabKey === OrderTabs.Ahead && !this.advancedFilter.values.SEPARATE_AHEAD;

    if (isClosedTabActiveButClosedDisabled || isAheadTabActiveButAheadNotSeparated) {
      this.setActiveTab(OrderTabs.All);
    }

    this._getOrdersWithNoDelay(0);
    this.fetchInitCounters();
  };

  handleUpdateOrder(data: any, event?: TOrderCRUDEvents) {
    const order = orderCast(data);
    const orderMeetsFilters = this._checkOrderMeetsFilters(order);
    const orderMeetsActiveTabFilter = this._checkOrderMeetsTabFilter({ ahead: order.ahead, status: order.order_status_code });
    this._updateOrder(order, orderMeetsFilters && orderMeetsActiveTabFilter);

    // update tab counters
    Object.keys(this._tabs).forEach((tabKey) => {
      if (tabKey === OrderTabs.All) {
        return;
      }

      const orderMeetsTabFilter = this._checkOrderMeetsTabFilter({ ahead: order.ahead, status: order.order_status_code }, tabKey as LiveOrdersTab);

      if (orderMeetsTabFilter && orderMeetsFilters && !order.viewed) {
        this._tabs[tabKey as LiveOrdersTab].newOrders.set(order.id, { id: order.id, ahead: order.ahead });

        this._addOrderToSound(order.id, event);
      } else if (this._tabs[tabKey as LiveOrdersTab].newOrders.has(order.id)) {
        this._tabs[tabKey as LiveOrdersTab].newOrders.delete(order.id);
        this._removeOrderForSound(order.id);
      }
    });
  }

  ignoreOrderForSound = (orderId: string) => {
    this._ignoreSoundOrdersIds.set(orderId, orderId);
  };

  _addOrderToSound = (orderId: string, event?: TOrderCRUDEvents) => {
    if (this._ignoreSoundOrdersIds.has(orderId)) {
      this._ignoreSoundOrdersIds.delete(orderId);
    } else if (!this._makeSoundOrdersIds.has(orderId)) {
      this._makeSoundOrdersIds.set(orderId, orderId);
      if (event && SOUND_TIMEOUT_MAP[event] && this._soundPlayTimeout <= SOUND_TIMEOUT_MAP[event]) {
        this._soundPlayTimeout = SOUND_TIMEOUT_MAP[event];
      }
    }
  };

  _removeOrderForSound = (orderId: string) => {
    this._makeSoundOrdersIds.delete(orderId);

    if (this._makeSoundOrdersIds.size === 0) {
      this._soundPlayTimeout = 0;
    }
  };

  _clearOrdersForSound = () => {
    this._makeSoundOrdersIds = observable.map();
    this._soundPlayTimeout = 0;
  };

  get soundPlayTimeout() {
    return this._soundPlayTimeout;
  }

  markOrderViewed = (orderId: string) => {
    this.lastOpenedOrderId = orderId;
    Object.keys(this._tabs).forEach((tabKey) => {
      if (this._tabs[tabKey as LiveOrdersTab].newOrders.has(orderId)) {
        this._tabs[tabKey as LiveOrdersTab].newOrders.delete(orderId);
      }
    });
    this._clearOrdersForSound();

    const order = this._orders.get(orderId);
    if (order) {
      this._orders.set(orderId, { ...order, viewed: 1 } as TOrder);
    }
  };

  fetchInitCounters = flow(function* (this: LiveOrdersStore, compareWithPrevCounters: boolean = false) {
    this.initTabCountersAbortController.abort();
    const initCountersAbortController = new AbortController();
    this.initTabCountersAbortController = initCountersAbortController;

    this._clearOrdersForSound();

    try {
      this.tabCountersStatus = LoadingStatus.Loading;

      const params = {
        branches: this.selectedBranches.map(({ id }) => id),
        is_delete: 0,
        due_start_date: this.advancedFilter.dateValues?.from,
        due_end_date: this.advancedFilter.dateValues?.to,
        payment_types: this.advancedFilter.values.PAYMENT_METHOD,
        delivery_types: this.advancedFilter.values.ORDER_TYPE,
        order_channels: this.advancedFilter.values.ORDERING_CHANNEL,
      };

      const response: { [key in ORDER_STATUS]: { order_id: number; ahead: number }[] } = yield client.get('/v3/orders/counters', {
        signal: this.initTabCountersAbortController.signal,
        params,
      });

      if (compareWithPrevCounters) {
        Object.keys(response)
          .reduce<{ order_id: number; ahead: number }[]>((prev, status) => {
            const prevCountersMap = this._prevCountersResponse?.[status as ORDER_STATUS] || observable.map();
            const newCounters = response?.[status as ORDER_STATUS] || [];

            newCounters.slice(-99).reverse();
            const newCountersForSound = newCounters.filter((newCounter) => !prevCountersMap.has(newCounter.order_id));

            this._prevCountersResponse[status as ORDER_STATUS] = observable.map(
              (response?.[status as ORDER_STATUS] || []).map((order) => [order.order_id, { order_id: order.order_id, ahead: order.ahead }])
            );

            return [...prev, ...newCountersForSound];
          }, [])
          .forEach(({ order_id }) => {
            const order = this._orders.get(String(order_id));
            const type = getOrderEventByNotificationType(order?.notification_type);

            this._addOrderToSound(String(order_id), type);
          });
      } else {
        Object.keys(response).forEach((status) => {
          this._prevCountersResponse[status as ORDER_STATUS] = observable.map(
            (response?.[status as ORDER_STATUS] || []).map((order) => [order.order_id, { order_id: order.order_id, ahead: order.ahead }])
          );
        });
      }

      Object.keys(this._tabs).forEach((tabKey) => {
        if (tabKey === OrderTabs.All) {
          return;
        }

        const filterByTab = getFilterByTab(
          tabKey as LiveOrdersTab,
          this.advancedFilter.values.SHOW_CLOSED,
          this.advancedFilter.values.SEPARATE_AHEAD
        );

        this._tabs[tabKey as LiveOrdersTab].newOrders = observable.map();

        filterByTab.status.map((tabStatus) => {
          response[tabStatus].forEach(({ ahead, order_id }) => {
            if (filterByTab.ahead === undefined || filterByTab.ahead === ahead) {
              this._tabs[tabKey as LiveOrdersTab].newOrders.set(String(order_id), { id: String(order_id), ahead });
            }
          });
        });
      });
      this.tabCountersStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (initCountersAbortController.signal.aborted) {
        this.tabCountersStatus = LoadingStatus.Aborted;
      } else {
        this.tabCountersStatus = LoadingStatus.Error;
        logger.error({ error, place: 'liveOrdersStore fetchInitCounters' });
      }
    }
  }).bind(this);

  get notViewedOrdersExists() {
    return this.tabs.some(([_, { newOrders }]) => newOrders.size > 0);
  }

  refreshDataAfterSocketReconnect = flow(function* (this: LiveOrdersStore) {
    yield this._getOrders(0);
    this.fetchInitCounters(true);
  }).bind(this);
}

export const liveOrdersStore = new LiveOrdersStore();

reaction(
  () => liveOrdersStore.soundPlayTimeout,
  (soundPlayTimeout) => {
    if (soundPlayTimeout && hasPermissions(USER_PERMISSIONS.ORDERS_MANAGMENT)) {
      player.stop();
      player.play(soundPlayTimeout);
    } else {
      player.stop();
    }
  }
);
